<template>
  <el-row class="d-flex">
    <el-button
      class="ml-auto mr-1"
      type="primary"
      :disabled="!userCanWriteEngagementGainImportConfiguration || isSignedOff"
      @click="showNewConfigurationModal = true"
    >
      <h4>{{ $t('add') }}</h4>
    </el-button>
    <el-button
      type="primary"
      :disabled="!userCanWriteEngagementGainImportConfiguration || isSignedOff"
      @click="recalculateApiNextRuns"
    >
      <h4>{{ $t('recalculateNextRuns') }}</h4>
    </el-button>
  </el-row>
  <el-row
    v-for="(item, index) in scheduledGainApiFileConfigurations"
    :key="index"
    class="mt-1"
  >
    <scheduled-api-file-configuration-card
      :configuration="item"
      :configuration-history="gainApiFileConfigurationHistory ?? []"
      :disable-delete-and-save="
        !userCanWriteEngagementGainImportConfiguration || isSignedOff
      "
      :api-connections="gainApiConnectionConfigurations"
      :gain-import-configuration="gainImportConfiguration"
      :exiting-output-file-names="existingFileNames(index)"
      :next-run-timestamp="getNextRunTimestamp(item.outputFileName)"
      @update="(configuration: ApiFileConfiguration) => updateEntry(index, configuration)"
      @delete="deleteEntry(index)"
      @save="saveEntry(index)"
      @fetch-history="fetchApiFileConfigurationHistory(item.configurationName!)"
    />
  </el-row>
  <new-api-file-configuration-modal
    :model-value="showNewConfigurationModal"
    :existing-names="
      allGainApiFileConfigurations.map(el => el.configurationName ?? '')
    "
    @close="showNewConfigurationModal = false"
    @confirm="handleConfigurationCreate"
  />
  <base-confirmation-modal
    v-model="showDeleteConfigurationModal"
    :feedback-text="$t('configurationDeleted')"
    @confirm="deleteConfiguration"
    @close="
      {
        configurationNameToBeDeleted = null;
        showDeleteConfigurationModal = false;
      }
    "
  >
    <template #content>
      <p>{{ $t('confirmDeleteConfiguration') }}</p>
      <p class="mt-2">
        <strong>{{ $t('confirmDeleteConfigurationNote') }}</strong>
      </p>
    </template>
  </base-confirmation-modal>
</template>

<script lang="ts">
import { ElRow, ElButton, ElMessage } from 'element-plus';
import { useStore } from 'vuex';
import { computed, ComputedRef, onActivated, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import i18n from '@/i18n';
import {
  GainImportConfiguration,
  RequirementsStatus,
  ApiFileConfiguration,
  ApiConnectionConfiguration,
  ApiNextRunConfiguration,
  ProcessType,
  ExecutionTrigger,
  ApiName
} from '@etp/etp-gain-bff-client/axios';
import ScheduledApiFileConfigurationCard from './components/ScheduledApiFileConfigurationCard.vue';
import NewApiFileConfigurationModal from './components/NewApiFileConfigurationModal.vue';
import BaseConfirmationModal from '@/components/modals/BaseConfirmationModal.vue';

export default {
  name: 'GainScheduledApiImportFiles',
  components: {
    ElRow,
    ElButton,
    ScheduledApiFileConfigurationCard,
    NewApiFileConfigurationModal,
    BaseConfirmationModal
  },
  setup() {
    const store = useStore();
    const router = useRouter();

    const showVersionModal = ref(false);
    const showNewConfigurationModal = ref(false);
    const showDeleteConfigurationModal = ref(false);
    const configurationNameToBeDeleted = ref<string | undefined | null>(null);

    const gainImportConfiguration: ComputedRef<GainImportConfiguration> =
      computed(() => store.getters['import/getGainImportConfiguration']);

    const gainApiConnectionConfigurations: ComputedRef<
      Array<ApiConnectionConfiguration>
    > = computed(
      () => store.getters['engagements/getApiConnectionConfigurations']
    );

    const isGainEngagement: ComputedRef<boolean> = computed(
      () => store.getters['home/isGainEngagement']
    );

    const isSignedOff: ComputedRef<boolean> = computed(
      () =>
        store.getters['gain/getCurrentRequirementsStatus']
          ?.requirementsStatus === RequirementsStatus.SignedOff
    );

    const scheduledGainApiFileConfigurations = ref(
      new Array<ApiFileConfiguration>()
    );
    const allGainApiFileConfigurations = ref(new Array<ApiFileConfiguration>());
    const gainApiFileConfigurationHistory = ref(
      new Array<ApiFileConfiguration>()
    );
    const apiNextRuns: ComputedRef<Array<ApiNextRunConfiguration>> = computed(
      () => store.getters['gain/getApiNextRuns']
    );

    const existingFileNames = (index: number) => {
      const otherApiNameFileNames = allGainApiFileConfigurations.value
        .filter(
          (config: ApiFileConfiguration) =>
            config.apiName !==
            scheduledGainApiFileConfigurations.value[index].apiName
        )
        .map(
          (config: ApiFileConfiguration) => config.outputFileName
        ) as string[];

      const sameApiNameFileNamesExceptIndex =
        scheduledGainApiFileConfigurations.value
          .filter((config: ApiFileConfiguration, idx: number) => idx !== index)
          .map(
            (config: ApiFileConfiguration) => config.outputFileName
          ) as string[];

      return [...otherApiNameFileNames, ...sameApiNameFileNamesExceptIndex];
    };

    const handleConfigurationCreate = (name: string) => {
      scheduledGainApiFileConfigurations.value.push({
        configurationName: name,
        apiName: ApiName.Etrade,
        executionTrigger: ExecutionTrigger.Schedule
      } as ApiFileConfiguration);
      showNewConfigurationModal.value = false;
    };
    const deleteEntry = (index: number) => {
      const configuration = scheduledGainApiFileConfigurations.value[index];
      if (configuration.version === undefined) {
        scheduledGainApiFileConfigurations.value.splice(index, 1);
      } else {
        showDeleteConfigurationModal.value = true;
        configurationNameToBeDeleted.value = configuration.configurationName;
      }
    };

    const deleteConfiguration = async () => {
      await store.dispatch(
        'import/deleteGainImportApiFileConfiguration',
        configurationNameToBeDeleted.value
      );
      configurationNameToBeDeleted.value = null;
      showDeleteConfigurationModal.value = false;
      await updateLocalVars();
    };
    const updateEntry = (
      index: number,
      configuration: ApiFileConfiguration
    ) => {
      scheduledGainApiFileConfigurations.value[index].outputFileName =
        configuration.outputFileName;
      scheduledGainApiFileConfigurations.value[index].apiName =
        configuration.apiName;
      scheduledGainApiFileConfigurations.value[index].executionTrigger =
        configuration.executionTrigger;
      scheduledGainApiFileConfigurations.value[index].numberOfRetries =
        configuration.numberOfRetries;
      scheduledGainApiFileConfigurations.value[index].retryDelay =
        configuration.retryDelay;
      scheduledGainApiFileConfigurations.value[index].timerTriggerConditions =
        configuration.timerTriggerConditions;
      scheduledGainApiFileConfigurations.value[index].apiSettings =
        configuration.apiSettings;
    };
    const saveEntry = async (index: number) => {
      const configuration = scheduledGainApiFileConfigurations.value[index];
      try {
        await store.dispatch(
          'import/createOrUpdateGainImportApiFileConfiguration',
          configuration
        );
        await updateLocalVars();
        ElMessage.success({
          message: i18n.global.t('apiFileConfigurationSaved'),
          showClose: true
        });
      } catch (error) {
        ElMessage.error({
          message: i18n.global.t('failedToSaveConfiguration'),
          showClose: true
        });
      }
    };

    const recalculateApiNextRuns = async () => {
      try {
        await store.dispatch('import/recalculateApiNextRuns');
        ElMessage.success({
          message: i18n.global.t('apiNextRunsRecalculated'),
          showClose: true
        });
      } catch (error) {
        ElMessage.error({
          message: i18n.global.t('errorOccured'),
          showClose: true
        });
      }
    };

    const fetchApiFileConfigurationHistory = async (
      configurationName: string
    ) => {
      if (configurationName) {
        await store.dispatch(
          'import/fetchGainImportApiFileConfigurationHistory',
          configurationName
        );
        gainApiFileConfigurationHistory.value =
          store.getters['import/getGainImportApiFileConfigurationHistory'];
      }
    };

    const getNextRunTimestamp = (
      outputFileName: string | undefined | null
    ): string => {
      if (!outputFileName) return '';
      const nextRun = apiNextRuns.value.find(
        run => run.outputFileName === outputFileName
      );
      return nextRun?.nextRunTimestamp ?? '';
    };

    const updateLocalVars = async () => {
      await Promise.all([
        store.dispatch('import/fetchGainImportApiFileConfigurations'),
        store.dispatch('gain/fetchApiNextRuns')
      ]);
      allGainApiFileConfigurations.value = [
        ...store.getters['import/getGainImportApiFileConfigurations']
      ];
      scheduledGainApiFileConfigurations.value =
        allGainApiFileConfigurations.value.filter(
          (config: ApiFileConfiguration) =>
            config.executionTrigger === ExecutionTrigger.Schedule
        );
    };
    const userCanWriteEngagementGainImportConfiguration: ComputedRef<boolean> =
      computed(
        () =>
          store.getters[
            'userPermissions/canWriteEngagementGainImportConfiguration'
          ]
      );

    onMounted(async () => {
      if (!isGainEngagement.value) router.push({ name: 'ImportMonitor' });
      const engagement = store.getters['home/getCurrentEngagement'];
      await store.dispatch('gain/fetchCurrentRequirementsStatus');
      await store.dispatch(
        'userPermissions/checkIfCanWriteEngagementGainImportConfiguration'
      );
      await store.dispatch(
        'engagements/fetchApiConnectionConfigurations',
        engagement.id
      );
      await store.dispatch('import/fetchGainImportConfiguration');
      await updateLocalVars();
    });

    onActivated(() => {
      store.dispatch('import/setSelectedProcessType', {
        processType: ProcessType.GainData
      });
    });

    return {
      allGainApiFileConfigurations,
      configurationNameToBeDeleted,
      deleteConfiguration,
      deleteEntry,
      existingFileNames,
      gainApiConnectionConfigurations,
      gainImportConfiguration,
      scheduledGainApiFileConfigurations,
      gainApiFileConfigurationHistory,
      getNextRunTimestamp,
      handleConfigurationCreate,
      isSignedOff,
      showDeleteConfigurationModal,
      showNewConfigurationModal,
      showVersionModal,
      updateEntry,
      saveEntry,
      recalculateApiNextRuns,
      fetchApiFileConfigurationHistory,
      userCanWriteEngagementGainImportConfiguration
    };
  }
};
</script>
